import React, { useCallback, useEffect, useState } from "react";
import { AudioProps } from "../../pages/Mint/types";
import fixWebmDuration from "webm-duration-fix";
import { toast } from "react-toastify";
import { AudioTarget } from "../AudioTarget/AudioTarget";
let interval: NodeJS.Timer;

const Audio: React.FC<AudioProps> = ({
  handleBlur,
  setFieldValue,
  touched,
  errors,
  isSubmitting,
  transferClearAudio,
  songTitle,
  songUrl,
  handleSongChanged,
  dawExportedBlob,
  determineBpm,
  setBpmLoading,
}) => {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [audio, setAudio] = useState<HTMLAudioElement | undefined | null>(
    undefined
  );
  const [filename, setFilename] = useState<string | undefined>("");
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [playingState, setPlayingState] = useState<string>("stop");

  useEffect(() => {
    if (songTitle && songUrl && playingState === "stop") {
      const fetchUrlToFile = async () => {
        const response = await fetch(songUrl);
        const songBlob = await response.blob();

        const file = new File([songBlob], songTitle, { type: songBlob.type });
        if (file) {
          setFieldValue("songUpload", file);
          setUrl(URL.createObjectURL(file));
          setAudio(document.querySelector("audio"));
          setFilename(songTitle);
        }
      };
      fetchUrlToFile();
    }
  }, [playingState, setFieldValue, songTitle, songUrl]);

  const audioHTML: () => Element | null = () =>
    document.querySelector(".audio-target");

  const handleAudioAdd = (file: any) => {
    setFieldValue("songUpload", file);
    setUrl(URL.createObjectURL(file));
    setAudio(document.querySelector("audio"));
    setFilename(file?.name);
    handleSongChanged && handleSongChanged();
    setFieldValue("isSongChanged", true);
    if (determineBpm && setBpmLoading) {
      determineBpm(file, setFieldValue, setBpmLoading);
    }
  };

  const onAddAudio = (event: React.ChangeEvent<HTMLInputElement>) => {
    pauseAudio();
    if (audio) {
      audio.currentTime = 0;
    }
    update();
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file) {
      handleAudioAdd(file);
    }
  };

  const onDragAudio = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      event.dataTransfer.files[0] &&
      event.dataTransfer.files[0].type.split("/")[0] !== "audio"
    ) {
      toast.info("Upload an audio file");
    } else {
      pauseAudio();
      if (audio) {
        audio.currentTime = 0;
      }
      update();
      const file = event.dataTransfer.files[0];
      // event.currentTarget.value = "";
      if (file) {
        handleAudioAdd(file);
      }
    }
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const updateMetadata = (e: any) => {
    const duration = e.target.duration;
    setAudioDuration(duration);
    const audioHTMLElement = audioHTML();
    if (audioHTMLElement) {
      audioHTMLElement.querySelector("input")!.max =
        Math.ceil(duration).toString();

      const minutes = Math.floor(duration / 60);

      const minuteElement = audioHTMLElement
        .querySelector(".duration")
        ?.querySelector(".minutes");
      if (minuteElement) {
        minuteElement.innerHTML = minutes.toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false,
        });
      }

      const secondsElement = audioHTMLElement
        .querySelector(".duration")
        ?.querySelector(".seconds");
      if (secondsElement) {
        secondsElement.innerHTML = Math.floor(
          duration - 60 * minutes
        ).toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false,
        });
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (dawExportedBlob) {
        const fixBlob = await fixWebmDuration(dawExportedBlob);
        setAudio(document.querySelector("audio"));
        setFieldValue("songUpload", new File([fixBlob], "Daw exported file"));
        setUrl(URL.createObjectURL(fixBlob));
        setFilename("Daw exported file");
      }
    })();
  }, [setFieldValue, dawExportedBlob]);

  const updateRangeBackground = () => {
    if (audio) {
      const audioHTMLElement = audioHTML();
      const delimiter = (audio.currentTime / audioDuration) * 100;
      let gradient = `
            linear-gradient(
                90deg,
                #1ED760 ${delimiter},
                #ccc ${delimiter}
            )
        `;
      gradient =
        "linear-gradient(90deg," +
        "#1ED760 " +
        delimiter +
        "%," +
        "#CCC " +
        delimiter +
        "%)";
      const inputElement = audioHTMLElement?.querySelector("input");
      if (inputElement) {
        inputElement.style.background = gradient;
      }
    }
  };

  const update = () => {
    const audioHTMLElement = audioHTML();
    updateRangeBackground();
    if (audio && audioHTMLElement) {
      const minutes = Math.floor(audio.currentTime / 60);
      const minuteElement = audioHTMLElement
        .querySelector(".playTime")
        ?.querySelector(".minutes");
      if (minuteElement) {
        minuteElement.innerHTML = minutes.toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false,
        });
      }
      const secondsElement = audioHTMLElement
        .querySelector(".playTime")
        ?.querySelector(".seconds");
      if (secondsElement) {
        secondsElement.innerHTML = Math.floor(
          audio.currentTime - 60 * minutes
        ).toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false,
        });
      }
    }
  };

  const playAudio = () => {
    const audioHTMLElement = audioHTML();
    if (!audio) return;
    if (audioHTMLElement) {
      audioHTMLElement.querySelector(".controls")?.classList.remove("restart");
      audioHTMLElement.querySelector(".controls")?.classList.add("playing");
      audio.play();
      setPlayingState("playing");
      clearInterval(interval);
      interval = setInterval(() => {
        update();
        const audioPointElement = audioHTMLElement.querySelector(
          "#audio-point"
        ) as HTMLInputElement;
        if (audioPointElement) {
          audioPointElement.value = audio.currentTime.toString();
          if (audio.duration === audio.currentTime) {
            clearInterval(interval);
            pauseAudio();
            setPlayingState("end");
            audioHTMLElement
              .querySelector(".controls")
              ?.classList.add("restart");
          }
        }
      }, 100);
    }
  };
  const pauseAudio = useCallback(() => {
    const audioHTMLElement = audioHTML();
    if (!audio) return;
    audio.pause();
    setPlayingState("stop");
    clearInterval(interval);
    audioHTMLElement?.querySelector(".controls")?.classList.remove("playing");
    audioHTMLElement?.querySelector(".controls")?.classList.remove("restart");
  }, [audio]);

  const reset = () => {
    // const { audio } = this.state;
    pauseAudio();
    if (audio) {
      audio.currentTime = 0;
    }
    update();
    playAudio();
  };

  const deleteAudio = useCallback(() => {
    pauseAudio();
    if (audio) {
      URL.revokeObjectURL(audio.src);
      setUrl(undefined);
      setAudio(undefined);
      setFilename(undefined);
    }
  }, [audio, pauseAudio]);

  useEffect(() => {
    transferClearAudio(deleteAudio);
  }, [deleteAudio, transferClearAudio]);

  return (
    <>
      <audio
        className="the_song"
        preload="metadata"
        style={{ display: "none" }}
        src={url ?? ""}
        onLoadedMetadata={updateMetadata}
      />
      <AudioTarget
        url={url}
        dawExportedBlob={dawExportedBlob}
        errors={errors}
        touched={touched}
        handleDragOver={handleDragOver}
        onDragAudio={onDragAudio}
        onAddAudio={onAddAudio}
        handleBlur={handleBlur}
        isSubmitting={isSubmitting}
        filename={filename}
        playingState={playingState}
        playAudio={playAudio}
        pauseAudio={pauseAudio}
        reset={reset}
      />
    </>
  );
};

export default Audio;
